import React, { useState } from "react";
import { Reply, Sparkles, Trash2, Loader2, Paperclip, Code2 } from "lucide-react";
import { analyzeMail, deleteMail } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { Badge, STATUS, fmtDate } from "./mailUtils";
import MailAgentCard from "./MailAgentCard";

const MailBody = ({ mail }) => {
  const [html, setHtml] = useState(false);
  if (mail.body_html && html) {
    return <iframe title="email" sandbox="" srcDoc={mail.body_html} className="w-full min-h-[320px] border border-neutral-200 rounded-lg bg-white" data-testid="mail-body-html" />;
  }
  return (
    <div>
      <pre className="whitespace-pre-wrap font-body text-sm text-neutral-800 leading-relaxed" data-testid="mail-body-text">{mail.body_text || "(message vide)"}</pre>
      {mail.body_html && (
        <button onClick={() => setHtml(true)} className="mt-2 text-xs text-brand flex items-center gap-1 hover:underline"><Code2 size={12} /> Afficher la version HTML</button>
      )}
    </div>
  );
};

const MailView = ({ mail, availability, onReply, onChanged }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const { toast } = useToast();
  const isInbox = mail.folder === "inbox";

  const analyze = async () => {
    setAnalyzing(true);
    try {
      await analyzeMail(mail.id);
      toast({ title: "Analyse terminée ✓" });
      onChanged();
    } catch (err) {
      toast({ title: "Analyse impossible", description: err?.response?.data?.detail || "Erreur IA", variant: "destructive" });
    } finally { setAnalyzing(false); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer cet email du CRM ?")) return;
    await deleteMail(mail.id);
    onChanged(true);
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4" data-testid="mail-view">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-lg text-neutral-900 break-words" data-testid="mail-view-subject">{mail.subject}</h3>
          <p className="text-sm text-neutral-600 mt-1">
            {isInbox ? <><b>{mail.from_name}</b> &lt;{mail.from_email}&gt;</> : <>À : <b>{mail.to}</b></>} · {fmtDate(mail.date)}
          </p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {isInbox && <Badge map={STATUS} value={mail.agent_status || "nouveau"} testid="mail-view-status" />}
            {mail.replied && <span className="text-[11px] text-sky-700 font-semibold">Réponse envoyée</span>}
            {mail.attachments?.map((a, i) => (
              <span key={i} className="text-[11px] text-neutral-500 flex items-center gap-1"><Paperclip size={11} /> {a.filename}</span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isInbox && (
            <>
              <button onClick={onReply} data-testid="mail-reply-btn" className="flex items-center gap-1.5 text-sm text-neutral-700 hover:text-brand px-2 py-1.5"><Reply size={16} /> Répondre</button>
              <button onClick={analyze} disabled={analyzing} data-testid="mail-analyze-btn"
                className="flex items-center gap-1.5 text-sm text-violet-700 hover:text-violet-900 px-2 py-1.5 disabled:opacity-60">
                {analyzing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {mail.ai ? "Ré-analyser" : "Analyser avec l'IA"}
              </button>
            </>
          )}
          <button onClick={remove} data-testid="mail-delete-btn" className="text-neutral-400 hover:text-red-500 p-1.5"><Trash2 size={16} /></button>
        </div>
      </div>

      {isInbox && mail.ai && <MailAgentCard mail={mail} availability={availability} onDone={() => onChanged()} />}
      {isInbox && mail.ai_error && <p className="text-sm text-red-600">Erreur IA : {mail.ai_error}</p>}

      <MailBody mail={mail} />

      {mail.replies?.length > 0 && (
        <div className="border-t border-neutral-200 pt-3 space-y-2">
          <p className="text-xs font-semibold text-neutral-500 uppercase">Vos réponses</p>
          {mail.replies.map((r) => (
            <div key={r.id} className="bg-neutral-50 rounded-lg p-3" data-testid={`mail-reply-${r.id}`}>
              <p className="text-xs text-neutral-500">{fmtDate(r.date)} · {r.sent_by === "agent" ? "Agent IA" : r.sent_by} {r.status === "error" && <span className="text-red-600">· échec</span>}</p>
              <pre className="whitespace-pre-wrap font-body text-sm text-neutral-800 mt-1">{r.body_text}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MailView;
